<script setup lang="ts">
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from 'element-plus'
import { computed, toRef } from 'vue'

import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, SelectorOptionRow, TypographyComponent } from '..'
import ChevronIcon from '../../assets/icons/chevron.svg?component'
import RefreshIcon from '../../assets/icons/refresh.svg?component'
import type { SelectorOption } from '../types/ui'

const props = withDefaults(
  defineProps<{
    options: SelectorOption[]
    selectedOption?: { id: number; label?: string } | null
    label?: string
    placeholder?: string
    shouldTruncateText?: boolean
    truncateOptionText?: boolean
    showSearch?: boolean
    loading?: boolean
    query?: string
    selectedLabel?: string
    showSelectedIcon?: boolean
    dropdownLabel?: string
    width?: string | number
    showClearButton?: boolean
    size?: 'default' | 'large'
    isDisabled?: boolean
    menuOverflow?: 'auto' | 'visible'
  }>(),
  {
    shouldTruncateText: false,
    truncateOptionText: undefined,
    showSearch: true,
    showSelectedIcon: true,
    showClearButton: false,
    size: 'default',
    isDisabled: false,
    menuOverflow: 'auto',
  },
)
const emits = defineEmits<{
  (e: 'update:selectedOption', value: SelectorOption | null): void
  (e: 'search', query: string): void
  (e: 'visible-change', visible: boolean): void
}>()

defineSlots<{
  option?: (props: { item: SelectorOption; selected: boolean }) => unknown
  labelSuffix?: () => unknown
}>()

const optionsRef = toRef(props, 'options')
const queryRef = toRef(props, 'query')

const displayedOptions = computed(() => optionsRef.value)
const shouldTruncateOptionText = computed(() => props.truncateOptionText ?? props.shouldTruncateText)

const displayLabel = computed(() => props.selectedLabel ?? props.label ?? props.placeholder ?? 'Select')

const sizeStyles = {
  default: { pt: 10, pr: 10, pb: 10, pl: 10, typography: 'label-large', iconSize: 18 },
  large: { pt: 16, pr: 12, pb: 16, pl: 16, typography: 'body-large', iconSize: 24 },
} as const

const currentSize = computed(() => sizeStyles[props.size])

const handleSelection = (item: SelectorOption) => {
  const isSame = props.selectedOption?.id === item.id
  emits('update:selectedOption', isSame ? null : item)
}

const handleVisible = (visible: boolean) => {
  emits('visible-change', visible)
}

const popperOptions = {
  modifiers: [
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite' as const,
      requires: ['computeStyles'] as string[],
      fn: ({ state }: { state: { elements: { popper: HTMLElement }; rects: { reference: { width: number } } } }) => {
        state.elements.popper.style.minWidth = `${state.rects.reference.width}px`
      },
    },
  ],
}
</script>

<template>
  <ElDropdown
    trigger="click"
    :disabled="isDisabled"
    :popper-options="popperOptions"
    :popper-class="menuOverflow === 'visible' ? 'selector-popper--overflow-visible' : undefined"
    @visible-change="handleVisible"
  >
    <BoxComponent
      :pt="currentSize.pt"
      :pr="currentSize.pr"
      :pb="currentSize.pb"
      :pl="currentSize.pl"
      :max-w="shouldTruncateText ? '15ch' : undefined"
      :title="displayLabel"
      :class="['selector-label', { 'selector-label--disabled': isDisabled }]"
    >
      <FlexComponent :gap="4" align="center">
        <TypographyComponent :variant="currentSize.typography" :text-truncate="shouldTruncateText">
          {{ displayLabel }}
        </TypographyComponent>
        <slot name="labelSuffix" />
        <ElIcon :size="currentSize.iconSize" class="chevron-icon">
          <ChevronIcon />
        </ElIcon>
      </FlexComponent>
    </BoxComponent>
    <template #dropdown>
      <BoxComponent :pt="8" :pb="8">
        <BoxComponent v-if="showSearch" :mb="16" :mt="8" :pr="16" :pl="16">
          <InputComponent
            size="small"
            :placeholder="placeholder ?? 'Search...'"
            :model-value="queryRef"
            @input="(val: string) => emits('search', val)"
          />
        </BoxComponent>
        <BoxComponent v-loading="loading" :min-w="120" :min-h="35" :w="width">
          <ElDropdownMenu
            v-if="displayedOptions.length > 0"
            size="large"
            :class="{ 'selector__menu--overflow-visible': menuOverflow === 'visible' }"
          >
            <BoxComponent v-if="dropdownLabel" :pt="8" :pb="8" :pl="16">
              <TypographyComponent variant="title-large">{{ dropdownLabel }}</TypographyComponent>
            </BoxComponent>
            <ElDropdownItem v-for="item in displayedOptions" :key="item.id" @click="handleSelection(item)">
              <slot name="option" :item="item" :selected="item.id === selectedOption?.id">
                <SelectorOptionRow
                  :label="item.label"
                  :selected="item.id === selectedOption?.id"
                  :truncate="shouldTruncateOptionText"
                  :show-selected-icon="showSelectedIcon"
                />
              </slot>
            </ElDropdownItem>
          </ElDropdownMenu>
          <ButtonComponent
            v-if="showClearButton && selectedOption"
            variant="text"
            class="selector__clear-button"
            @click="emits('update:selectedOption', null)"
          >
            <template #iconLeft>
              <ElIcon :size="24">
                <RefreshIcon />
              </ElIcon>
            </template>
            Clear filter
          </ButtonComponent>
          <BoxComponent v-if="!loading && displayedOptions.length === 0" :pt="10" :pb="10">
            <TypographyComponent is="p" variant="body-small" text-align="center">No matching data</TypographyComponent>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    </template>
  </ElDropdown>
</template>

<style lang="scss" scoped>
:deep(.el-dropdown-menu) {
  max-height: 440px;

  overflow: auto;
}
:deep(.el-dropdown-menu.selector__menu--overflow-visible) {
  max-height: none;

  overflow: visible;

  /* Rounded to 4px, so it would paint outside the popper's 16px corner. The popper paints the
   * same colour underneath. */
  background: none;

  /* Element Plus's own 5px would keep the end rows clear of the corner meant to cut them. */
  padding: 0;

  /* Pushed back out over the menu's 8px padding so the clip lands on the popper's own corner. A row
   * that owns a submenu passes the clip to its state layer, leaving the submenu outside it. */
  .el-dropdown-menu__item:first-of-type {
    --option-end-clip: inset(-8px 0 0 0 round 16px 16px 0 0);
  }

  .el-dropdown-menu__item:last-of-type {
    --option-end-clip: inset(0 0 -8px 0 round 0 0 16px 16px);
  }

  .el-dropdown-menu__item:first-of-type:last-of-type {
    --option-end-clip: inset(-8px 0 round 16px);
  }

  .el-dropdown-menu__item:not(:has([aria-haspopup='menu'])) {
    clip-path: var(--option-end-clip, none);
  }

  /* The row paints its own state layer, so the item under it stays clear. */
  .el-dropdown-menu__item:has([aria-haspopup='menu']):hover,
  .el-dropdown-menu__item:has([aria-haspopup='menu']):focus {
    background: transparent;
  }
}

:deep(.el-dropdown-menu__item) {
  padding: 0;
  white-space: normal;
}
:deep(.el-input--small .el-input__wrapper) {
  padding: 8px;
}

.button.selector__clear-button {
  width: 100%;
  border-radius: 0 0 16px 16px;
  padding: 16px;
  margin-bottom: -8px;
  height: 56px;
  border-top: 1px solid var(--button-hover);
  justify-content: flex-start;
  font-weight: 400;
}

/* Figma disabled field: keeps its border, takes a tinted fill, value drops to 48%. */
.selector-label--disabled {
  background: rgba(34, 34, 34, 0.04);
  cursor: not-allowed;

  :deep(.text-color),
  .chevron-icon {
    opacity: 0.48;
  }
}
</style>
<style lang="scss">
/* Unscoped on purpose: the popper lives outside this component, and Element Plus's own scrollbar
 * there clips a submenu rendered from the option slot. */
.selector-popper--overflow-visible {
  .el-scrollbar,
  .el-scrollbar__wrap {
    overflow: visible;
  }

  .el-scrollbar__bar {
    display: none;
  }
}
</style>
